import React, { useEffect, useRef, useState } from 'react';
import { Music, Calendar, Clock, MapPin } from 'lucide-react';
import { getFutureBands, getNextBandDateLabel } from '../utils/bandDateUtils';
import { restaurantInfo } from '../config/siteConfig';

const LiveMusicSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Get only future bands (past dates are automatically filtered out)
  const futureBands = getFutureBands();
  const featuredEvent = futureBands[0];
  const upcomingEvents = futureBands.slice(1);

  return (
    <section
      id="live-music"
      ref={sectionRef}
      className="py-20 lg:py-28 bg-[#1a1612] relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Subtle spotlight effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#C9A227]/10 rounded-full blur-[100px]" />
      </div>

      {/* Top decorative line */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4A574] to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-5">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="text-[#D4A574] font-semibold tracking-widest text-sm uppercase">{getNextBandDateLabel()}</span>
          </div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#FDF6E9] tracking-wide leading-[0.95]">
            LIVE MUSIC
          </h2>
          <p className="mt-4 text-[#FDF6E9]/50 text-lg max-w-xl mx-auto">
            Join us for live performances from local artists.
          </p>
        </div>

        {/* Featured Event - Large Card */}
        {featuredEvent && (
          <div className={`mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div className="relative bg-gradient-to-br from-[#C9A227]/20 to-[#2C2416] border-2 border-[#D4A574]/30 p-8 sm:p-10 overflow-hidden music-card-featured">
              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A227]/20 rounded-bl-full" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-[#D4A574]/20 rounded-full" />
              
              <div className="relative">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="stamp text-[#D4A574] text-xs animate-pulse">UP NEXT</span>
                      <span className="text-[#FDF6E9]/40 text-sm">{featuredEvent.genre}</span>
                    </div>
                    <h3 className="font-display text-4xl sm:text-5xl text-[#FDF6E9] tracking-wide mb-3">
                      {featuredEvent.band}
                    </h3>
                    <p className="text-[#FDF6E9]/60 text-lg mb-4">
                      {featuredEvent.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 text-[#D4A574]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        <span className="font-semibold">{featuredEvent.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        <span>{featuredEvent.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        <span>Satchmo's</span>
                      </div>
                    </div>
                    {/* Event Link Button */}
                    {featuredEvent.eventLink && (
                      <a 
                        href={featuredEvent.eventLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#C9A227] text-[#1a1612] font-display text-lg tracking-wide hover:bg-[#d4ad2e] transition-colors"
                      >
                        VIEW EVENT
                      </a>
                    )}
                  </div>
                  
                  {/* Visual element */}
                  <div className="flex-shrink-0 hidden lg:block">
                    <div className="w-48 h-48 relative">
                      <div className="absolute inset-0 bg-[#C9A227]/20 rounded-full animate-pulse" />
                      <div className="absolute inset-4 bg-[#2C2416] rounded-full flex items-center justify-center">
                        <Music className="w-16 h-16 text-[#D4A574]" />
                      </div>
                      {/* Animated rings */}
                      <div className="absolute inset-0 border-2 border-[#D4A574]/20 rounded-full motion-safe:animate-ping" style={{ animationDuration: '2s' }} />
                      <div className="absolute inset-2 border border-[#D4A574]/10 rounded-full motion-safe:animate-ping" style={{ animationDuration: '2.5s' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty state — no upcoming shows on the calendar yet */}
        {!featuredEvent && (
          <div className={`mb-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div className="relative bg-gradient-to-br from-[#C9A227]/10 to-[#2C2416] border-2 border-[#D4A574]/20 p-10 text-center overflow-hidden">
              {/* Atmospheric backdrop: the bar itself, kept dark so text stays readable */}
              <img
                src="/gallery/bar-interior.jpg"
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-25 img-vintage"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#1a1612]/70 via-[#1a1612]/55 to-[#1a1612]/80" />
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A227]/10 rounded-bl-full" />
              <div className="relative">
              <Music className="w-10 h-10 text-[#D4A574]/60 mx-auto mb-4" />
              <h3 className="font-display text-3xl text-[#FDF6E9] tracking-wide">
                NEXT LINEUP DROPS SOON
              </h3>
              <p className="mt-3 text-[#FDF6E9]/50 max-w-md mx-auto">
                We're booking the next round of live music — most Saturdays, 6PM.
                Follow us on Facebook to be first to know.
              </p>
              <a
                href={restaurantInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#C9A227] text-[#1a1612] font-display text-lg tracking-wide hover:bg-[#d4ad2e] transition-colors"
              >
                FOLLOW FOR UPDATES
              </a>
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Events Grid */}
        {upcomingEvents.length > 0 && (
          <div className={`transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl text-[#FDF6E9] tracking-wide">COMING UP</h3>
            </div>

            {/* Events Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingEvents.map((event, index) => (
                <div
                  key={index}
                  className="bg-[#FDF6E9]/5 border border-[#D4A574]/20 p-6 hover:bg-[#FDF6E9]/10 hover:border-[#D4A574]/40 transition-all duration-300 group hover:-translate-y-1 hover:shadow-lg hover:shadow-[#C9A227]/10"
                  style={{ animationDelay: `${400 + index * 100}ms` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-[#D4A574] text-xs font-semibold tracking-wide uppercase">
                      {event.genre}
                    </span>
                    <Music className="w-5 h-5 text-[#D4A574]/50 group-hover:text-[#D4A574] transition-colors" />
                  </div>
                  <h4 className="font-display text-xl text-[#FDF6E9] tracking-wide mb-2 group-hover:text-[#D4A574] transition-colors">
                    {event.band}
                  </h4>
                  <p className="text-[#FDF6E9]/50 text-sm mb-4 line-clamp-2">
                    {event.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-[#FDF6E9]/60">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{event.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-[#FDF6E9]/40 text-sm mb-4">No cover charge • All ages welcome</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={`tel:${restaurantInfo.phone.replace(/[^0-9]/g, '')}`} className="btn-retro">
              CALL FOR INFO
            </a>
            <button 
              onClick={() => document.getElementById('hours').scrollIntoView({ behavior: 'smooth' })}
              className="text-[#D4A574] font-display text-lg tracking-wide hover:text-[#FDF6E9] transition-colors underline underline-offset-4"
            >
              GET DIRECTIONS →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveMusicSection;
